import { useCallback, useState } from 'react';
import {
  useSignInWithApple,
  useSignInWithGoogle,
} from '@/contexts/auth';
import { AuthModalState } from '@/types/auth-types';
import type { AuthModalStateTarget } from './authModalStateTypes';

export interface ProviderAuthHandlers {
  loadingApple: boolean;
  loadingGoogle: boolean;
  handleAppleAuth: () => void;
  handleGoogleAuth: () => void;
}

export function useProviderAuthHandlers({
  setError,
  setModalState,
}: AuthModalStateTarget): ProviderAuthHandlers {
  const signInWithGoogle = useSignInWithGoogle();
  const signInWithApple = useSignInWithApple();
  const [loadingApple, setLoadingApple] = useState(false);
  const [loadingGoogle, setLoadingGoogle] = useState(false);

  const handleAppleAuth = useCallback((): void => {
    setLoadingApple(true);
    setError(null);

    signInWithApple.invoke(undefined, {
      onError: (authError) => {
        setModalState(AuthModalState.Error);
        setError({
          title: 'Apple Sign-In Failed',
          message: authError instanceof Error
            ? authError.message
            : 'Unable to authenticate with Apple. Please try again.',
        });
        setLoadingApple(false);
      },
    });
  }, [setError, setModalState, signInWithApple]);

  const handleGoogleAuth = useCallback((): void => {
    setLoadingGoogle(true);
    setError(null);

    signInWithGoogle.invoke(undefined, {
      onError: (authError) => {
        setModalState(AuthModalState.Error);
        setError({
          title: 'Google Sign-In Failed',
          message: authError instanceof Error
            ? authError.message
            : 'Unable to authenticate with Google. Please try again.',
        });
        setLoadingGoogle(false);
      },
    });
  }, [setError, setModalState, signInWithGoogle]);

  return {
    loadingApple,
    loadingGoogle,
    handleAppleAuth,
    handleGoogleAuth,
  };
}
